import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import ToggleOffRoundedIcon from "@mui/icons-material/ToggleOffRounded";
import StoreMallDirectoryOutlinedIcon from "@mui/icons-material/StoreMallDirectoryOutlined";
import {
  getStores,
  getStoreDetail,
  setStoreApproval,
  setStoreActiveStatus,
  setStoreIsPrime,
  setStoreComingSoon,
} from "../../api/adminService";
import { getStoreColumns, StoreDetailDrawer } from "./StoreShared";

const TABS = [
  { value: "all", label: "All", icon: StorefrontRoundedIcon, color: "primary" },
  { value: "pending", label: "Pending Approval", icon: PendingActionsRoundedIcon, color: "warning" },
  { value: "approved", label: "Approved", icon: VerifiedRoundedIcon, color: "success" },
  { value: "inactive", label: "Inactive", icon: ToggleOffRoundedIcon, color: "error" },
];

export default function Stores() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [detail, setDetail] = useState(null);
  const [counts, setCounts] = useState({});

  const loadStores = useCallback(() => {
    setLoading(true);
    getStores({
      status,
      search: search || undefined,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    })
      .then((data) => {
        setRows(data.items);
        setRowCount(data.totalCount);
        setError("");
      })
      .catch(() => setError("Could not load stores. Is the API running?"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, search, paginationModel]);

  const loadCounts = useCallback(() => {
    Promise.all(TABS.map((tab) => getStores({ status: tab.value, page: 1, pageSize: 1 })))
      .then((results) => {
        const next = {};
        TABS.forEach((tab, i) => {
          next[tab.value] = results[i].totalCount;
        });
        setCounts(next);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, paginationModel]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  function handleSearchSubmit(e) {
    e.preventDefault();
    setPaginationModel((p) => ({ ...p, page: 0 }));
    loadStores();
  }

  const openDetail = useCallback(async (storeId) => {
    const data = await getStoreDetail(storeId);
    setDetail(data);
  }, []);

  const handleApprove = useCallback(
    async (storeId, isApproved) => {
      await setStoreApproval(storeId, isApproved);
      loadStores();
      loadCounts();
      if (detail?.storeId === storeId) openDetail(storeId);
    },
    [loadStores, loadCounts, detail, openDetail]
  );

  const handleToggleActive = useCallback(
    async (storeId, isActive) => {
      await setStoreActiveStatus(storeId, isActive);
      loadStores();
      loadCounts();
      if (detail?.storeId === storeId) openDetail(storeId);
    },
    [loadStores, loadCounts, detail, openDetail]
  );

  const handleToggleIsPrime = useCallback(
    async (storeId, isPrime) => {
      await setStoreIsPrime(storeId, isPrime);
      loadStores();
      if (detail?.storeId === storeId) openDetail(storeId);
    },
    [loadStores, detail, openDetail]
  );

  const handleToggleComingSoon = useCallback(
    async (storeId, isComingSoon) => {
      await setStoreComingSoon(storeId, isComingSoon);
      loadStores();
      if (detail?.storeId === storeId) openDetail(storeId);
    },
    [loadStores, detail, openDetail]
  );

  const handleViewAnalytics = useCallback(
    (storeId) => navigate(`/admin/analytics/stores/${storeId}`),
    [navigate]
  );

  const handlers = useMemo(
    () => ({ handleApprove, handleToggleActive, handleToggleIsPrime, handleToggleComingSoon, handleViewAnalytics }),
    [handleApprove, handleToggleActive, handleToggleIsPrime, handleToggleComingSoon, handleViewAnalytics]
  );

  const columns = useMemo(() => getStoreColumns(handlers), [handlers]);

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{
          mb: 3,
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Avatar sx={{ bgcolor: "primary.main", width: 44, height: 44 }}>
            <StoreMallDirectoryOutlinedIcon />
          </Avatar>
          <Box>
            <Typography variant="h4">Stores</Typography>
            <Typography variant="body2" color="text.secondary">
              Review approvals, manage delivery partners, and control storefront visibility.
            </Typography>
          </Box>
        </Stack>
        <Tooltip title="Refresh">
          <IconButton
            onClick={() => {
              loadStores();
              loadCounts();
            }}
            sx={{ border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
          >
            <RefreshRoundedIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 2.5, overflow: "hidden" }}>
        <Tabs
          value={status}
          onChange={(e, v) => {
            setStatus(v);
            setPaginationModel((p) => ({ ...p, page: 0 }));
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
        >
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <Tab
                key={tab.value}
                value={tab.value}
                iconPosition="start"
                icon={<Icon sx={{ fontSize: 18 }} />}
                label={
                  <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                    <span>{tab.label}</span>
                    <Chip
                      size="small"
                      label={counts[tab.value] ?? "…"}
                      color={status === tab.value ? tab.color : "default"}
                      variant={status === tab.value ? "filled" : "outlined"}
                      sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                    />
                  </Stack>
                }
                sx={{ minHeight: 52, textTransform: "none" }}
              />
            );
          })}
        </Tabs>

        <Stack
          component="form"
          onSubmit={handleSearchSubmit}
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{
            p: 2,
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
          }}
        >
          <TextField
            placeholder="Search by store name or owner…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ minWidth: 320 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearch("");
                        setPaginationModel((p) => ({ ...p, page: 0 }));
                      }}
                    >
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {rowCount.toLocaleString()} store{rowCount === 1 ? "" : "s"}
          </Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.storeId}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          rowHeight={64}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          onRowClick={(params) => openDetail(params.row.storeId)}
          sx={{ border: "none" }}
          slots={{
            noRowsOverlay: () => (
              <Stack spacing={1} sx={{ py: 6, alignItems: "center", justifyContent: "center" }}>
                <StorefrontRoundedIcon sx={{ fontSize: 36, color: "text.disabled" }} />
                <Typography variant="body2" color="text.secondary">
                  No stores match this view.
                </Typography>
              </Stack>
            ),
          }}
        />
      </Paper>

      <StoreDetailDrawer
        detail={detail}
        onClose={() => setDetail(null)}
        handlers={handlers}
        onSaved={() => {
          loadStores();
          openDetail(detail.storeId);
        }}
      />
    </Box>
  );
}
